import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  BarChart3,
  BookOpen,
  Check,
  Coins,
  Layout,
  Lightbulb,
  Link2,
  Sparkles,
  User as UserIcon,
  Users,
  Wallet,
  X,
} from "lucide-react";
import type { AuthUser } from "../lib/api";
import { navigate, usePath } from "../lib/router";

/* =============================================================================
 * Tour-Steps — jede einzelne Agent-Relevante Seite wird erklärt
 * ============================================================================*/

type Step = {
  id: string;
  path: string;        // wohin navigieren
  title: string;
  icon: typeof Sparkles;
  body: React.ReactNode;
  fullOnly?: boolean;  // nur Voll-Agent
};

const STORAGE_KEY_PREFIX = "dp-portal-tour-done:";

function isCompleted(userId: string): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_PREFIX + userId) === "1";
  } catch {
    return true;
  }
}
function markCompleted(userId: string): void {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + userId, "1");
  } catch {
    /* noop */
  }
}
export function resetPortalTour(userId: string): void {
  try {
    localStorage.removeItem(STORAGE_KEY_PREFIX + userId);
  } catch {
    /* noop */
  }
}

const STEPS: Step[] = [
  {
    id: "hub",
    path: "/",
    title: "Willkommen im Hub",
    icon: Sparkles,
    body: (
      <>
        <p>
          Das hier ist deine <strong>Übersicht</strong>. Oben drei Zahlen: Reserviert,
          Freigegeben, letzte 30 Tage. Darunter alle Bereiche als Karten.
        </p>
        <p>
          Wir gehen jetzt jede wichtige Seite durch — pro Schritt navigiere ich dich
          automatisch dorthin und erkläre dir was du da siehst.
        </p>
      </>
    ),
  },
  {
    id: "links",
    path: "/links",
    title: "Empfehlungs-Links + Shop-Modus",
    icon: Link2,
    body: (
      <>
        <p>
          Hier ist alles für deinen Link. Ganz oben siehst du die <strong>orange Card
          „Shop-Modus starten"</strong> — der schnellste Weg zum Link-Erstellen.
        </p>
        <p>
          <strong>Wie der Shop-Modus funktioniert:</strong> Klick auf den Knopf → du
          landest auf dpconnect.de. Unten links (Handy) oder rechts (Computer) erscheint
          ein orangefarbener Floating-Knopf. Bei jedem Produkt drauf klicken → Link
          kopieren — fertig.
        </p>
        <p>
          Drunter dein <strong>Standard-Link</strong>, der Produkt-Such-Bereich, und die
          Möglichkeit, eigene Kampagnen-Codes anzulegen (z.B. einer für Insta, einer
          für TikTok).
        </p>
      </>
    ),
  },
  {
    id: "page-editor",
    path: "/page-editor",
    title: "Deine Landing-Page",
    icon: Layout,
    body: (
      <>
        <p>
          Deine eigene Mini-Webseite unter <code className="font-mono text-xs">deincode.dpconnect.de</code>.
        </p>
        <p>
          <strong>Wichtige Cards von oben nach unten:</strong>
        </p>
        <ul className="list-disc list-inside space-y-0.5">
          <li><strong>Veröffentlichen</strong> — Toggle muss grün sein, sonst sieht's keiner</li>
          <li><strong>QR-Code</strong> — Download für Print</li>
          <li><strong>Bilder</strong> — Profilfoto + Cover-Bild hochladen</li>
          <li><strong>Theme</strong> — 8 Farbstimmungen zur Auswahl</li>
          <li><strong>Profil & Begrüßung</strong> — Bio mit **fett**/*kursiv*</li>
          <li><strong>Inhalts-Blöcke</strong> — Text, Video, Abstand einfügen</li>
          <li><strong>Modus</strong> — Hand-kuratiert oder Kompletter Shop</li>
          <li><strong>Produkte</strong> — mit Stern als Top-Pick markieren</li>
        </ul>
        <p className="text-xs">
          Am Computer siehst du <strong>rechts die Live-Vorschau</strong>.
        </p>
      </>
    ),
  },
  {
    id: "commissions",
    path: "/commissions",
    title: "Provisionen-Übersicht",
    icon: Coins,
    body: (
      <>
        <p>
          Hier siehst du <strong>jede einzelne Provision</strong> die du verdient hast,
          mit Status (reserviert / freigegeben / ausgezahlt / storniert).
        </p>
        <p>
          Filter oben nach Status oder Tier (1 = direkter Verkauf, 2 = dein Team, 3 = Team
          deines Teams).
        </p>
      </>
    ),
  },
  {
    id: "payouts",
    path: "/payouts",
    title: "Auszahlungen",
    icon: Wallet,
    body: (
      <>
        <p>
          Monatliche Übersicht aller <strong>tatsächlichen Auszahlungen</strong>. Bei
          jeder kannst du die Gutschrift als PDF herunterladen.
        </p>
        <p>
          Ganz oben siehst du die <strong>nächste geplante Auszahlung</strong> — wieviel
          du gerade aufgebaut hast und wann es kommt.
        </p>
      </>
    ),
  },
  {
    id: "stats",
    path: "/stats",
    title: "Statistik",
    icon: BarChart3,
    body: (
      <>
        <p>
          Was funktioniert, was nicht? Hier siehst du:
        </p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>Anzahl Klicks pro Link / Kampagnen-Code</li>
          <li>Conversion-Rate (wie viele Klicker am Ende gekauft haben)</li>
          <li>Top-Produkte aus deinen Verkäufen</li>
        </ul>
        <p className="text-xs">
          Auf der Landing-Page selbst zeigt der Editor pro Produkt eine Klick-Zahl —
          gucke nach, was deine Besucher anklicken.
        </p>
      </>
    ),
  },
  {
    id: "team",
    path: "/team",
    title: "Mein Team",
    icon: Users,
    fullOnly: true,
    body: (
      <>
        <p>
          <em>Nur Voll-Agenten.</em>
        </p>
        <p>
          Dein <strong>Recruitment-Link</strong> ist ganz oben. Den teilst du mit
          Menschen die selbst Agent werden wollen.
        </p>
        <p>
          Darunter siehst du dein Team (Tier 2) und das Team deines Teams (Tier 3), mit
          den jeweiligen Verkaufs- und Provisions-Zahlen.
        </p>
      </>
    ),
  },
  {
    id: "account",
    path: "/account",
    title: "Stammdaten + Passwort",
    icon: UserIcon,
    body: (
      <>
        <p>
          Hier kannst du:
        </p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>Email + Name ändern</li>
          <li>Passwort neu setzen (aktuelles + neues)</li>
          <li>Bank-Verbindung pflegen (für Auszahlungen)</li>
          <li>USt-ID hinterlegen (Voll-Agent)</li>
        </ul>
      </>
    ),
  },
  {
    id: "chat",
    path: "/",
    title: "Hilfe-Chatbot — unten rechts",
    icon: Lightbulb,
    body: (
      <>
        <p>
          Du siehst unten rechts einen <strong>kleinen Sprechblasen-Knopf</strong>. Klick
          drauf, dann öffnet sich dein DP-Helfer.
        </p>
        <p>
          <strong>Zwei Modi:</strong>
        </p>
        <ul className="list-disc list-inside space-y-0.5">
          <li><strong>Frage stellen</strong> — der Bot beantwortet alles über das Portal</li>
          <li><strong>Idee teilen</strong> — was wünschst du dir? Wir lesen und antworten</li>
        </ul>
      </>
    ),
  },
  {
    id: "hilfe",
    path: "/hilfe",
    title: "Vollständige Anleitung",
    icon: BookOpen,
    body: (
      <>
        <p>
          Wenn du was im Detail nachlesen willst — hier ist die komplette Erklärung mit
          Inhaltsverzeichnis. Auch ausdruckbar.
        </p>
        <p>
          Du kannst die Tour <strong>jederzeit oben rechts wieder starten</strong>.
        </p>
      </>
    ),
  },
  {
    id: "upgrade",
    path: "/upgrade",
    title: "Light → Voll-Agent",
    icon: ArrowUp,
    body: (
      <>
        <p>
          <em>Nur Light-Agenten.</em>
        </p>
        <p>
          Wenn du ein Gewerbe hast (oder anmeldest), kannst du auf Voll-Agent upgraden.
          Vorteile:
        </p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>höhere Provisions-Sätze</li>
          <li>eigenes Team aufbauen (Tier 2 + 3)</li>
          <li>eigene Empfehlungs-Seite mit Subdomain</li>
        </ul>
      </>
    ),
  },
];

/* =============================================================================
 * Context + Provider
 * ============================================================================*/

type TourCtx = {
  active: boolean;
  start: () => void;
  stop: () => void;
};

const Ctx = createContext<TourCtx | null>(null);

export function useTour(): TourCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useTour must be inside TourProvider");
  return c;
}

export function TourProvider({
  user,
  children,
}: {
  user: AuthUser;
  children: React.ReactNode;
}) {
  const [active, setActive] = useState<boolean>(() => !isCompleted(user.id));
  const [stepIdx, setStepIdx] = useState(0);
  const path = usePath();

  const isFullAgent = user.agent_type === "full";
  const isLightAgent = user.agent_type === "light";

  // gefilterte Steps je nach Agent-Type
  const steps = STEPS.filter((s) => {
    if (s.id === "team" && !isFullAgent) return false;
    if (s.id === "upgrade" && !isLightAgent) return false;
    return true;
  });

  // Navigiere zur Pfad-Seite des aktuellen Schritts, sobald Schritt wechselt
  useEffect(() => {
    if (!active) return;
    const step = steps[stepIdx];
    if (!step) return;
    if (path !== step.path) navigate(step.path);
    // wir wollen NICHT auf path-Änderungen reagieren — Browser-Navigation soll Tour nicht
    // schon weiterschalten
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx, active]);

  const start = useCallback(() => {
    resetPortalTour(user.id);
    setStepIdx(0);
    setActive(true);
  }, [user.id]);

  const stop = useCallback(() => {
    markCompleted(user.id);
    setActive(false);
  }, [user.id]);

  const next = () => {
    if (stepIdx >= steps.length - 1) {
      stop();
    } else {
      setStepIdx((i) => i + 1);
    }
  };
  const prev = () => setStepIdx((i) => Math.max(0, i - 1));

  // Body-Flag fuer FAB-Stacking — andere FABs koennen sich verbergen
  useEffect(() => {
    if (active) document.body.setAttribute("data-tour-active", "1");
    else document.body.removeAttribute("data-tour-active");
    return () => document.body.removeAttribute("data-tour-active");
  }, [active]);

  return (
    <Ctx.Provider value={{ active, start, stop }}>
      {children}
      {active && (
        <TourBar
          step={steps[stepIdx]}
          current={stepIdx + 1}
          total={steps.length}
          onNext={next}
          onPrev={prev}
          onClose={stop}
          isFirst={stepIdx === 0}
          isLast={stepIdx === steps.length - 1}
        />
      )}
    </Ctx.Provider>
  );
}

/* =============================================================================
 * TourBar — schwebende Karte am unteren Rand
 * ============================================================================*/

function TourBar({
  step,
  current,
  total,
  onNext,
  onPrev,
  onClose,
  isFirst,
  isLast,
}: {
  step: Step;
  current: number;
  total: number;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
  isFirst: boolean;
  isLast: boolean;
}) {
  const Icon = step.icon;
  return (
    <div className="fab-bottom-center z-50 w-[min(560px,calc(100vw-2rem))] print:hidden">
      <div className="rounded-2xl bg-white shadow-2xl border border-line overflow-hidden">
        {/* Progress */}
        <div className="h-1 bg-cream-soft">
          <div
            className="h-full brand-gradient transition-all"
            style={{ width: `${(current / total) * 100}%` }}
          />
        </div>

        <div className="p-4">
          <div className="flex items-start gap-3 mb-2">
            <div className="grid size-9 place-items-center rounded-xl brand-gradient text-white flex-shrink-0">
              <Icon className="size-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wide font-semibold text-ink-muted">
                Tour · Schritt {current} von {total}
              </div>
              <h3 className="text-base font-semibold leading-tight">{step.title}</h3>
            </div>
            <button
              onClick={onClose}
              className="size-7 grid place-items-center rounded-full text-ink-muted hover:bg-cream-soft hover:text-ink flex-shrink-0"
              title="Tour beenden"
            >
              <X className="size-4" />
            </button>
          </div>

          <div className="text-sm text-ink-soft leading-relaxed space-y-2 max-h-[40vh] overflow-y-auto pr-1">
            {step.body}
          </div>

          <div className="mt-3 flex items-center justify-between gap-2">
            <button
              onClick={onPrev}
              disabled={isFirst}
              className="inline-flex items-center gap-1 text-xs text-ink-muted disabled:opacity-30 hover:text-ink"
            >
              <ArrowLeft className="size-3" /> Zurück
            </button>
            <button
              onClick={onClose}
              className="text-xs text-ink-muted hover:text-ink underline"
            >
              Tour überspringen
            </button>
            <button
              onClick={onNext}
              className="inline-flex items-center gap-1 rounded-lg brand-gradient text-white px-3 py-1.5 text-sm font-medium hover:brightness-110"
            >
              {isLast ? (
                <>
                  Fertig <Check className="size-3.5" />
                </>
              ) : (
                <>
                  Weiter <ArrowRight className="size-3.5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
